import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ApiService } from 'src/app/services/api.service';

/** Left-column "company snapshot" card on the business page (wireframe member
 *  panel). Shown to visitors and to admins in Member View. It only draws —
 *  the data and the Follow/Unfollow/Message actions come from the banner, so
 *  both places stay in sync. */
@Component({
  selector: 'app-business-member-card',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './business-member-card.component.html',
  styleUrls: ['./business-member-card.component.scss']
})
export class BusinessMemberCardComponent implements OnChanges {
  /** `business/details` → businessModelList[0] */
  @Input() business: any;
  /** `business/check/admin` response (isFollow, isFollowerInvited, …) */
  @Input() admindata: any;
  /** Route query params (isAdmin / isSuperAdmin as 'true' | 'false') */
  @Input() businessData: any;
  @Input() logoSrc: string;

  @Output() follow = new EventEmitter<void>();
  @Output() unfollow = new EventEmitter<void>();
  @Output() message = new EventEmitter<void>();
  @Output() viewJobs = new EventEmitter<void>();

  openRoles = 0;
  private jobsForBusinessId: string;

  constructor(private api: ApiService) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.business) {
      this.socials = this.buildSocials();
    }
    const id = this.business?.businessId;
    if (changes.business && id && id !== this.jobsForBusinessId) {
      this.jobsForBusinessId = id;
      this.loadOpenRoles(id);
    }
  }

  // Same call as the Jobs tab; limit 1 because only the total is needed.
  private loadOpenRoles(businessId: string): void {
    const payload = {
      limit: 1,
      searchAfterKey: null,
      searchContent: '',
      userId: localStorage.getItem('userId'),
      businessId
    };
    this.api.create('jobs/filter', payload).subscribe(res => {
      if (businessId !== this.jobsForBusinessId) { return; }
      this.openRoles = res?.data?.totalJobsCount || 0;
    });
  }

  get location(): any {
    return this.business?.companyLocationDetails?.[0];
  }

  get locationText(): string {
    const l = this.location;
    return [l?.city, l?.state, l?.country].filter(Boolean).join(', ');
  }

  get phone(): string {
    return this.location?.businessPhone || this.business?.phone;
  }

  get email(): string {
    return this.location?.businessEmail || this.business?.businessEmail;
  }

  /** Visitor = not an admin of this business (Follow/Message only apply then). */
  get isVisitor(): boolean {
    return this.businessData?.isAdmin == 'false' && this.businessData?.isSuperAdmin == 'false';
  }

  /** Admin previewing in Member View: Follow/Message show, but disabled. */
  get isPreview(): boolean {
    return (this.businessData?.isAdmin == 'true' || this.businessData?.isSuperAdmin == 'true')
      && this.businessData?.viewingAsMember == 'true';
  }

  get canFollow(): boolean {
    return this.isVisitor && this.admindata != null && !this.admindata?.isFollow && !this.admindata?.isFollowerInvited;
  }

  get canUnfollow(): boolean {
    return this.isVisitor && this.admindata != null && this.admindata?.isFollow;
  }

  get canMessage(): boolean {
    return this.isVisitor && this.admindata != null;
  }

  /** Brief "Link copied" label after the clipboard fallback. */
  shareCopied = false;
  private shareCopiedTimer: any;

  /** Share the business page. Same link shape as job-profile-slider's onShare:
   *  /auth/login with redirectTo + data, so logged-out recipients land on the
   *  page after login and logged-in ones are bounced straight there. Only the
   *  businessId goes in — never the sharer's isAdmin/isSuperAdmin params.
   *  Web Share API → falls back to clipboard. Works in Member View preview too. */
  shareBusiness(): void {
    const businessId = this.business?.businessId;
    if (!businessId) { return; }
    // Hash routing (environment.useHash = true) needs `/#/` before /auth/login.
    const url = `${window.location.origin}/#/auth/login`
      + `?redirectTo=/business/post`
      + `&absolutePath=true`
      + `&data=${encodeURIComponent(JSON.stringify({ businessId }))}`;
    const title = this.business?.businessName || 'Business';
    const nav: any = window.navigator;
    if (nav?.share) {
      nav.share({ title, url }).catch(() => { /* user cancelled */ });
    } else if (nav?.clipboard) {
      nav.clipboard.writeText(url).then(() => {
        this.shareCopied = true;
        clearTimeout(this.shareCopiedTimer);
        this.shareCopiedTimer = setTimeout(() => this.shareCopied = false, 2000);
      });
    }
  }

  /** Built once per business change — a getter returning a new array made
   *  *ngFor recreate the icons on every change detection (hover blinked). */
  socials: { key: string, label: string, url: string }[] = [];

  trackByKey(_: number, s: { key: string }): string {
    return s.key;
  }

  private buildSocials(): { key: string, label: string, url: string }[] {
    const s = this.business?.userSocialPresence || {};
    return [
      { key: 'linkedin', label: 'LinkedIn', url: s.linkedin },
      { key: 'twitter', label: 'X / Twitter', url: s.twitter },
      { key: 'youtube', label: 'YouTube', url: s.youtube },
      { key: 'instagram', label: 'Instagram', url: s.instagram },
      { key: 'facebook', label: 'Facebook', url: s.facebook },
      { key: 'tiktok', label: 'TikTok', url: s.tiktok }
    ].filter(x => !!x.url).map(x => ({ ...x, url: this.withProtocol(x.url) }));
  }

  withProtocol(url: string): string {
    return /^https?:\/\//i.test(url) ? url : 'http://' + url;
  }
}
